import type {
  EvaluationPlanCategoryBundle,
  EvaluationPlanRowScope,
  EvaluationPlanWorkspaceDraft,
} from '../types/evaluationPlan';
import { buildEvaluationPlanBundleVersion } from './evaluationPlanCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';
import {
  idbDeleteEvaluationPlanBundle,
  idbGetEvaluationPlanBundle,
  idbPutEvaluationPlanBundle,
} from './evaluationPlanBundleIdb';

/** v3: contentVersion(head) 비교용 메타 저장 */
const BUNDLE_CACHE_VERSION = 'v3';
const CATEGORIES_CACHE_VERSION = 'v3';
const WORKSPACE_SESSION_VERSION = 'v2';

function bundleStorageKey(workspaceId: string, gradeId: string, categoryName: string): string {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  return `cp.workspaceEvaluationPlan.bundle.${BUNDLE_CACHE_VERSION}.${ws}.${gid}.${encodeURIComponent(name)}`;
}

function categoriesStorageKey(workspaceId: string, gradeId: string): string {
  return `cp.workspaceEvaluationPlan.categories.${CATEGORIES_CACHE_VERSION}.${workspaceId.trim()}.${gradeId.trim()}`;
}

function normalizeBundle(raw: unknown): EvaluationPlanCategoryBundle | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Partial<EvaluationPlanCategoryBundle>;
  if (typeof obj.categoryName !== 'string') return null;
  return {
    categoryName: obj.categoryName,
    standards: Array.isArray(obj.standards) ? obj.standards : [],
    plans: Array.isArray(obj.plans) ? obj.plans : [],
  };
}

export type CachedWorkspaceEvaluationPlanCategory = {
  id: string;
  category_name: string;
  sort_order: number;
  level_step_count: number;
  level_label_a?: string;
  level_label_b?: string;
  level_label_c?: string;
  level_label_d?: string;
  level_label_e?: string;
  updated_at?: string;
};

/**
 * 팀·개인 번들 캐시: 메모리(동기 조회) ← IndexedDB(영속). 관리자 번들과 같은 방식.
 * 예전 LocalStorage(deviceCacheStorage) 항목은 첫 조회 시 IDB 로 이관 후 삭제.
 */
const memBundles = new Map<string, { bundle: EvaluationPlanCategoryBundle; contentVersion: string }>();

function memKey(ws: string, gid: string, name: string): string {
  return `ws:${ws}:${gid}:${name}`;
}

export async function hydrateWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): Promise<void> {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name) return;
  const key = memKey(ws, gid, name);
  if (memBundles.has(key)) return;
  const entry = await idbGetEvaluationPlanBundle(key);
  if (entry && !memBundles.has(key)) {
    memBundles.set(key, { bundle: { ...entry.bundle, categoryName: name }, contentVersion: entry.contentVersion });
  }
}

export function loadWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): { found: boolean; bundle: EvaluationPlanCategoryBundle | null; contentVersion: string | null } {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name || typeof window === 'undefined') {
    return { found: false, bundle: null, contentVersion: null };
  }
  const mem = memBundles.get(memKey(ws, gid, name));
  if (mem) return { found: true, bundle: mem.bundle, contentVersion: mem.contentVersion };
  try {
    const raw = deviceCacheStorage.getItem(bundleStorageKey(ws, gid, name));
    if (!raw) return { found: false, bundle: null, contentVersion: null };
    const parsed = JSON.parse(raw) as { bundle?: unknown; contentVersion?: string };
    const bundle = normalizeBundle(parsed?.bundle ?? parsed);
    if (!bundle) return { found: false, bundle: null, contentVersion: null };
    const stored =
      typeof parsed?.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    const result = {
      found: true,
      bundle: { ...bundle, categoryName: name },
      contentVersion: stored || buildEvaluationPlanBundleVersion({ ...bundle, categoryName: name }),
    };
    /** 레거시 LS → IDB 이관 */
    saveWorkspaceEvaluationPlanBundleCache(ws, gid, name, result.bundle, {
      force: true,
      contentVersion: result.contentVersion,
    });
    return result;
  } catch {
    return { found: false, bundle: null, contentVersion: null };
  }
}

export function saveWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  bundle: EvaluationPlanCategoryBundle,
  options?: { force?: boolean; contentVersion?: string }
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(ws)) return;
  const next = { ...bundle, categoryName: name };
  const contentVersion =
    (options?.contentVersion ?? '').trim() || buildEvaluationPlanBundleVersion(next);
  const key = memKey(ws, gid, name);
  memBundles.set(key, { bundle: next, contentVersion });
  void idbPutEvaluationPlanBundle(key, { bundle: next, contentVersion, savedAt: Date.now() });
  try {
    deviceCacheStorage.removeItem(bundleStorageKey(ws, gid, name));
  } catch {
    /* noop */
  }
}

export function clearWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name || typeof window === 'undefined') return;
  const key = memKey(ws, gid, name);
  memBundles.delete(key);
  void idbDeleteEvaluationPlanBundle(key);
  try {
    deviceCacheStorage.removeItem(bundleStorageKey(ws, gid, name));
  } catch {
    /* noop */
  }
}

export function patchWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  patch: (prev: EvaluationPlanCategoryBundle) => EvaluationPlanCategoryBundle
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name || typeof window === 'undefined') return;
  const cached = loadWorkspaceEvaluationPlanBundleCache(ws, gid, name);
  if (!cached.found || !cached.bundle) return;
  saveWorkspaceEvaluationPlanBundleCache(ws, gid, name, patch({ ...cached.bundle, categoryName: name }), {
    force: true,
  });
}

export function renameWorkspaceEvaluationPlanBundleCache(
  workspaceId: string,
  gradeId: string,
  fromName: string,
  toName: string
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const from = fromName.trim();
  const to = toName.trim();
  if (!ws || !gid || !from || !to || from === to || typeof window === 'undefined') return;
  const cached = loadWorkspaceEvaluationPlanBundleCache(ws, gid, from);
  if (!cached.found || !cached.bundle) {
    clearWorkspaceEvaluationPlanBundleCache(ws, gid, from);
    return;
  }
  saveWorkspaceEvaluationPlanBundleCache(
    ws,
    gid,
    to,
    { ...cached.bundle, categoryName: to },
    { force: true, contentVersion: cached.contentVersion ?? undefined }
  );
  clearWorkspaceEvaluationPlanBundleCache(ws, gid, from);
}

export function loadWorkspaceEvaluationPlanCategoriesCache(
  workspaceId: string,
  gradeId: string
): {
  found: boolean;
  categories: CachedWorkspaceEvaluationPlanCategory[];
  contentVersion: string | null;
} {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') {
    return { found: false, categories: [], contentVersion: null };
  }
  try {
    const raw = deviceCacheStorage.getItem(categoriesStorageKey(ws, gid));
    if (!raw) return { found: false, categories: [], contentVersion: null };
    const parsed = JSON.parse(raw) as { categories?: unknown; contentVersion?: string };
    const list = Array.isArray(parsed?.categories) ? parsed.categories : [];
    const categories = list.filter(
      (row): row is CachedWorkspaceEvaluationPlanCategory =>
        !!row &&
        typeof row === 'object' &&
        typeof (row as CachedWorkspaceEvaluationPlanCategory).category_name === 'string'
    );
    const stored =
      typeof parsed.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    return {
      found: true,
      categories,
      contentVersion: stored || buildLearningContentScopeVersion(categories),
    };
  } catch {
    return { found: false, categories: [], contentVersion: null };
  }
}

export function saveWorkspaceEvaluationPlanCategoriesCache(
  workspaceId: string,
  gradeId: string,
  categories: CachedWorkspaceEvaluationPlanCategory[],
  options?: { force?: boolean; contentVersion?: string }
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(ws)) return;
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(categories);
    deviceCacheStorage.setItem(
      categoriesStorageKey(ws, gid),
      JSON.stringify({ categories, contentVersion, savedAt: Date.now() })
    );
  } catch {
    /* noop */
  }
}

export function patchWorkspaceEvaluationPlanCategoriesCache(
  workspaceId: string,
  gradeId: string,
  patch: (list: CachedWorkspaceEvaluationPlanCategory[]) => CachedWorkspaceEvaluationPlanCategory[]
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') return;
  const cached = loadWorkspaceEvaluationPlanCategoriesCache(ws, gid);
  const next = patch(cached.found ? cached.categories : []);
  saveWorkspaceEvaluationPlanCategoriesCache(ws, gid, next, { force: true });
}

function workspaceSessionStorageKey(workspaceId: string, gradeId: string): string {
  return `cp.workspaceEvaluationPlan.workspaceSession.${WORKSPACE_SESSION_VERSION}.${workspaceId.trim()}.${gradeId.trim()}`;
}

export type WorkspaceEvaluationPlanSessionEntry = {
  draft: EvaluationPlanWorkspaceDraft;
  baselineJson: string;
  planScopeById?: Record<string, EvaluationPlanRowScope>;
  standardScopeById?: Record<string, EvaluationPlanRowScope>;
  standardsReadOnly?: boolean;
};

export type WorkspaceEvaluationPlanSessionState = {
  draftsByCategory: Record<string, WorkspaceEvaluationPlanSessionEntry>;
  updatedAt: number;
};

export function loadWorkspaceEvaluationPlanSessionState(
  workspaceId: string,
  gradeId: string
): WorkspaceEvaluationPlanSessionState | null {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(workspaceSessionStorageKey(ws, gid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WorkspaceEvaluationPlanSessionState>;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      draftsByCategory:
        parsed.draftsByCategory && typeof parsed.draftsByCategory === 'object'
          ? (parsed.draftsByCategory as Record<string, WorkspaceEvaluationPlanSessionEntry>)
          : {},
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function saveWorkspaceEvaluationPlanSessionEntry(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  entry: WorkspaceEvaluationPlanSessionEntry
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const name = categoryName.trim();
  if (!ws || !gid || !name || typeof window === 'undefined') return;
  try {
    const prev = loadWorkspaceEvaluationPlanSessionState(ws, gid);
    const draftsByCategory = { ...(prev?.draftsByCategory ?? {}), [name]: entry };
    window.sessionStorage.setItem(
      workspaceSessionStorageKey(ws, gid),
      JSON.stringify({ draftsByCategory, updatedAt: Date.now() } satisfies WorkspaceEvaluationPlanSessionState)
    );
  } catch {
    /* noop */
  }
}
